import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Target, Compass, Eye, Rocket, Users, Shield, Lightbulb, TrendingUp } from 'lucide-react';

export const About = () => {
  const { t } = useLanguage();

  const values = [
    { key: 'innovation', icon: Rocket, color: 'text-orange-400' },
    { key: 'commitment', icon: Users, color: 'text-blue-400' },
    { key: 'trust', icon: Shield, color: 'text-green-400' },
    { key: 'creativity', icon: Lightbulb, color: 'text-yellow-400' },
    { key: 'scalability', icon: TrendingUp, color: 'text-purple-400' },
  ];

  return (
    <section id="about" className="py-20 lg:py-32 px-4 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <h2 className="section-title">{t('about.title')}</h2>
          <p className="text-lg text-gray-400 max-w-3xl leading-relaxed">
            {t('about.description')}
          </p>
        </motion.div>

        {/* Purpose, Mission, Vision */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {['purpose', 'mission', 'vision'].map((item, index) => {
            const icons = { purpose: Target, mission: Compass, vision: Eye };
            const colors = { purpose: 'text-orange-400', mission: 'text-blue-400', vision: 'text-cyan-400' };
            const Icon = icons[item];
            
            return (
              <motion.div
                key={item}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass rounded-2xl p-6 lg:p-8"
                data-testid={`about-${item}`}
              >
                <div className={`inline-flex p-3 rounded-xl bg-white/5 mb-4`}>
                  <Icon className={`w-6 h-6 ${colors[item]}`} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  {t(`about.${item}.title`)}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {t(`about.${item}.text`)}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Values */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h3 className="text-2xl font-bold text-white mb-8">{t('about.values.title')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.key}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="value-card card-hover"
                  data-testid={`value-${value.key}`}
                >
                  <Icon className={`w-8 h-8 ${value.color} mx-auto mb-3`} />
                  <p className="text-sm text-gray-300 font-medium">
                    {t(`about.values.${value.key}`)}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
